import { Injectable } from '@angular/core'
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects'
import { catchError, combineLatest, EMPTY, filter, of, switchMap, tap } from 'rxjs'
import { DatasetApiService } from '../../shared/services/dataset-api.service'
import { plotDataActions } from './plot-data.actions'
import { Store } from '@ngrx/store'
import { selectCaseId } from '../case/case.selectors'
import {
    resetPlotData,
    setPlotDataForAxis,
    setPlotDataForBothAxes,
    filterPlotData,
    setPlotDataType,
    setMultipleInstanceData,
} from './plot-data'
import { ToasterService } from '../../shared/services/toaster.service'
import { caseActions } from '../case/case.actions'
import { selectionActions } from '../selection/selection.actions'
import {
    selectFilterPoints,
    selectDisplayedSelectionsOnScatterPlot,
} from '../selection/selection.selectors'
import { SelectionType } from '../selection/selection.state'

@Injectable()
export class PlotDataEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly datasetApi: DatasetApiService,
        private readonly store: Store,
        private readonly toaster: ToasterService
    ) {}

    loadSegObjectPropertiesData$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(caseActions.loadingSegObjectPropertiesDataSucceeded),
            switchMap(({ segobjectpropertiesdata }) => {
                if (segobjectpropertiesdata.length < 2) return EMPTY

                return of(
                    plotDataActions.loadDataBothAxes({
                        markerXAxis: segobjectpropertiesdata[0],
                        markerYAxis: segobjectpropertiesdata[1],
                    })
                )
            })
        )
    })

    loadDataForAxis$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(plotDataActions.loadDataForAxis),
                concatLatestFrom(() => this.store.select(selectCaseId)),
                filter(([, caseId]) => !!caseId),
                switchMap(([{ axis, marker }, caseId]) =>
                    marker === null
                        ? of(setPlotDataForAxis(null, axis))
                        : this.datasetApi.getDataForDimension(marker, caseId!).pipe(
                              tap(data => setPlotDataForAxis(data, axis)),
                              catchError(err => {
                                  this.toaster.httpError(err, 'Error loading plot data.')
                                  return EMPTY
                              })
                          )
                )
            )
        },
        { dispatch: false }
    )

    loadDataForBothAxes$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(plotDataActions.loadDataBothAxes),
                concatLatestFrom(() => this.store.select(selectCaseId)),
                filter(([, caseId]) => !!caseId),
                switchMap(([action, caseId]) => {
                    return combineLatest([
                        this.datasetApi.getDataForDimension(action.markerXAxis, caseId!),
                        this.datasetApi.getDataForDimension(action.markerYAxis, caseId!),
                    ]).pipe(
                        catchError(err => {
                            this.toaster.httpError(err, 'Error loading plot data.')
                            return EMPTY
                        })
                    )
                }),
                tap(([dataX, dataY]) => {
                    setPlotDataForBothAxes(dataX, dataY)
                })
            )
        },
        { dispatch: false }
    )

    filterPlotData$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(
                    selectionActions.setType,
                    selectionActions.saveNewHierarchicalSelectionRoot,
                    selectionActions.savingHierarchicalSelectionRootSucceeded,
                    selectionActions.setHierarchicalSelectionRoot,
                    selectionActions.setHierarchicalSelectionNode,
                    selectionActions.setAutoCellTypeDetectionRoot,
                    selectionActions.setAutoCellTypeDetectionNode,
                    selectionActions.saveNewAutoCellTypeDetectionNode,
                    selectionActions.savingAutoCellTypeDetectionRootSucceeded
                ),
                concatLatestFrom(() => this.store.select(selectFilterPoints)),
                tap(([_, filterPoints]) =>
                    filterPlotData(filterPoints.pointsThisLevel, filterPoints.pointsLowerLevels)
                )
            )
        },
        { dispatch: false }
    )

    setPlotDataType$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(selectionActions.setType),
                tap(selectionType => {
                    const plotType =
                        selectionType.selectionType == SelectionType.Free
                            ? 0
                            : selectionType.selectionType == SelectionType.Hierarchical
                            ? 1
                            : 2
                    setPlotDataType(plotType)
                })
            )
        },
        { dispatch: false }
    )

    resetPlotState$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(caseActions.selectCase),
                tap(() => resetPlotData())
            )
        },
        { dispatch: false }
    )

    loadMultipleInstanceData$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(selectionActions.setVisibleSelectionsOnScatterPlot),
                concatLatestFrom(() => this.store.select(selectDisplayedSelectionsOnScatterPlot)),
                tap(([_, selections]) => {
                    setMultipleInstanceData(selections)
                })
            )
        },
        { dispatch: false }
    )
}
